<template>
  <UserDocumentsList
    :documents="store.personalDocuments"
    :available-types="availableDocumentTypes"
    :hide-upload="isUploadHidden"
    :on-download="onDownload"
    :on-upload="uploadFile"/>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { errorNotification } from '@/_redesign/shared/ui/NotificationPopup/notification-popup';
import {
  BUSINESS_ACTOR_DOCUMENTS,
  PROOF_OF_IDENTITY_DOCUMENTS,
  TWO_SIDE_DOCUMENTS,
} from '@/constants';
import { useCustomerProfileInject } from '@/modules/onboarding-dashboard/shared/utils';
import UserDocumentsList from '@/modules/onboarding-user-profile/components/user-documents-list.vue';
import { OnboardingAdminRequests } from '@/services/requests';
import { usePersonalOnboardingStore } from '@/stores/OnboardingPersonal';
import { CorporateDocumentType, DocumentSide } from '@/types/interfaces/Document.interface';

const store = usePersonalOnboardingStore();
const { isApplicationEditable } = useCustomerProfileInject();

const { params: { id } } = useRoute();

const isPersonalDetailsFilled = computed(() => store.completedStages.includes('PERSONAL_DETAILS'));
const isUploadHidden = computed(() => !(isApplicationEditable.value && isPersonalDetailsFilled.value));

const availableDocumentTypes = computed(() => {
  const documentArray = PROOF_OF_IDENTITY_DOCUMENTS.map((document) => {
    if (TWO_SIDE_DOCUMENTS.includes(document as typeof TWO_SIDE_DOCUMENTS[0])) {
      return [`${document}_FRONT`, `${document}_BACK`];
    }
    return [document];
  }).flat();
  return [...BUSINESS_ACTOR_DOCUMENTS, ...documentArray];
});

async function onDownload(type: CorporateDocumentType, side: DocumentSide): Promise<Blob | undefined> {
  const { response, error } = await OnboardingAdminRequests.Personal.getPersonalDocument(id, { type, side });

  if (error) {
    errorNotification(error);
    return undefined;
  }

  return response;
}

const uploadFile = async (fileData: {
  type: CorporateDocumentType;
  formData: FormData;
  issuingCountryIso3?: string;
  side?: DocumentSide;
}) => {
  await store.personalDetailsAdminReq.uploadDocument(id, fileData);
};

</script>
